'use client';

import type {ComponentPropsWithoutRef} from 'react';

import {getImageStackForConfiguration, type UnionProductConfiguration} from './configuration';
import type {ProductImageStackDescription} from './ProductImageStack';
import {ProductImageStack} from './ProductImageStack';
import type {UnionProductFragment} from './query';

export const PRODUCT_IMAGE_SIDES = ['front', 'back', 'left', 'right', 'top', 'bottom'] as const;

export type ProductImageSide = (typeof PRODUCT_IMAGE_SIDES)[number];

export function isProductImageSide(value?: string): value is ProductImageSide {
	return value !== undefined && (PRODUCT_IMAGE_SIDES as readonly string[]).includes(value);
}

type ProductImageProps = ComponentPropsWithoutRef<'div'> & {
	product: UnionProductFragment;
	config: UnionProductConfiguration;
	allowPlaceholders?: boolean;
};

/**
 * Displays the single most representative product image for the given product and configuration.
 * Preferably the front or top image, but it will fall back to any of the other sides if it needs
 * to.
 */
export default function ProductImage({
	product,
	config,
	allowPlaceholders = false,
	...rest
}: ProductImageProps) {
	let stack: ProductImageStackDescription | null = null;

	for (const side of PRODUCT_IMAGE_SIDES) {
		stack = getImageStackForConfiguration(side, product, config, allowPlaceholders);
		if (stack) {
			break;
		}
	}

	if (!stack) {
		return null;
	}

	return <ProductImageStack stack={stack} {...rest} />;
}
